import { getApiUrl } from "@/utils/api";
/**
 * Lightweight analytics tracker for Sell Local Homes
 * GDPR-compliant: only fires when cookie_consent === "all"
 * Tracks: page views, scroll depth, time on page, session lifecycle
 */
import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = getApiUrl();

// ─── Device Detection Helpers ─────────────────────────────────────────────────

function getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|blackberry|iemobile|opera mini|android(?!.*tablet)/i.test(ua))
    return "mobile";
  return "desktop";
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes("Edg/")) return "Edge";
  if (ua.includes("OPR/") || ua.includes("Opera/")) return "Opera";
  if (ua.includes("Chrome/")) return "Chrome";
  if (ua.includes("Firefox/")) return "Firefox";
  if (ua.includes("Safari/")) return "Safari";
  return "Other";
}

function getOS() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  if (/Macintosh/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}

function getLocaleInfo() {
  try {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || "";
    const country = lang.includes("-") ? lang.split("-")[1].toUpperCase() : "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const city = tz.includes("/") ? tz.split("/").pop().replace(/_/g, " ") : "";
    return { country, city };
  } catch {
    return { country: "", city: "" };
  }
}

function getTrafficSource(referrer) {
  if (!referrer) {
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_medium") === "email") return "email";
    if (params.get("utm_medium") === "cpc" || params.get("utm_medium") === "paid") return "paid";
    return "direct";
  }
  if (/google\.|bing\.|yahoo\.|duckduckgo\.|ecosia\./i.test(referrer)) return "organic";
  if (/facebook\.|twitter\.|instagram\.|linkedin\.|tiktok\.|youtube\.|pinterest\./i.test(referrer))
    return "social";
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_medium") === "cpc" || params.get("utm_medium") === "paid") return "paid";
  if (params.get("utm_medium") === "email") return "email";
  return "referral";
}

// ─── Persistent Identity ──────────────────────────────────────────────────────

function getVisitorId() {
  let vid = localStorage.getItem("_slh_vid");
  if (!vid) {
    vid = "v_" + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem("_slh_vid", vid);
  }
  return vid;
}

function getSessionId() {
  let sid = sessionStorage.getItem("_slh_sid");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem("_slh_sid", sid);
  }
  return sid;
}

function hasConsent() {
  return localStorage.getItem("cookie_consent") === "all";
}

// ─── React Hook ───────────────────────────────────────────────────────────────

export function useAnalyticsTracker() {
  const location = useLocation();
  const pageEnterTime = useRef(null);
  const scrollMilestones = useRef(new Set());
  const sessionStarted = useRef(false);

  const sendEvent = useCallback(async (eventData) => {
    if (!hasConsent()) return;
    const { country, city } = getLocaleInfo();
    const params = new URLSearchParams(window.location.search);
    try {
      await axios.post(`${API}/analytics/event`, {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        device_type: getDeviceType(),
        browser: getBrowser(),
        os_name: getOS(),
        country,
        city,
        referrer: document.referrer || "",
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
        traffic_source: getTrafficSource(document.referrer),
        ...eventData,
      });
    } catch {
      // Silent fail — analytics must never affect UX
    }
  }, []);

  // Track page view + session on every route change
  useEffect(() => {
    if (!hasConsent()) return;

    pageEnterTime.current = Date.now();
    scrollMilestones.current = new Set();

    const { country, city } = getLocaleInfo();
    const params = new URLSearchParams(window.location.search);
    const sid = getSessionId();
    const vid = getVisitorId();
    const referrer = document.referrer || "";
    const trafficSource = getTrafficSource(referrer);

    // Page view event
    sendEvent({
      event_type: "page_view",
      page_url: location.pathname + location.search,
      page_title: document.title,
    });

    // Session management
    const sessionAction = sessionStarted.current ? "heartbeat" : "start";
    if (!sessionStarted.current) sessionStarted.current = true;

    axios
      .post(`${API}/analytics/session`, {
        session_id: sid,
        visitor_id: vid,
        action: sessionAction,
        page_url: location.pathname,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os_name: getOS(),
        country,
        city,
        referrer,
        traffic_source: trafficSource,
        utm_source: params.get("utm_source") || "",
        utm_campaign: params.get("utm_campaign") || "",
      })
      .catch(() => {});

    // Time-on-page tracking (fire before navigating away)
    const handleUnload = () => {
      const timeOnPage = Math.round((Date.now() - pageEnterTime.current) / 1000);
      // Use sendBeacon for reliability on unload
      const payload = JSON.stringify({
        event_type: "page_exit",
        page_url: location.pathname,
        time_on_page: timeOnPage,
        session_id: sid,
        visitor_id: vid,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os_name: getOS(),
        country,
        city,
        referrer,
        traffic_source: trafficSource,
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${API}/analytics/event`,
          new Blob([payload], { type: "application/json" })
        );
        // Also end session
        const sessionPayload = JSON.stringify({
          session_id: sid,
          visitor_id: vid,
          action: "heartbeat",
          page_url: location.pathname,
          duration_seconds: Math.round(
            (Date.now() - pageEnterTime.current) / 1000
          ),
        });
        navigator.sendBeacon(
          `${API}/analytics/session`,
          new Blob([sessionPayload], { type: "application/json" })
        );
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [location.pathname, location.search, sendEvent]);

  // Scroll depth milestones
  useEffect(() => {
    if (!hasConsent()) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);

      [25, 50, 75, 100].forEach((milestone) => {
        if (pct >= milestone && !scrollMilestones.current.has(milestone)) {
          scrollMilestones.current.add(milestone);
          sendEvent({
            event_type: "scroll_depth",
            page_url: location.pathname,
            scroll_depth_pct: milestone,
          });
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname, sendEvent]);

  return { sendEvent };
}

// ─── Global trackEvent (for use outside React components) ────────────────────

export function trackEvent(eventType, data = {}) {
  if (!hasConsent()) return;
  const { country, city } = getLocaleInfo();
  const params = new URLSearchParams(window.location.search);
  const payload = {
    event_type: eventType,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    page_url: window.location.pathname,
    device_type: getDeviceType(),
    browser: getBrowser(),
    os_name: getOS(),
    country,
    city,
    referrer: document.referrer || "",
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    traffic_source: getTrafficSource(document.referrer),
    ...data,
  };
  axios.post(`${API}/analytics/event`, payload).catch(() => {});
}
